import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  FileText,
  CheckCircle,
  Edit2,
  Eraser,
  AlertCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SigningPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [signedAt, setSignedAt] = useState(null);
  
  // Signature state
  const [signatureType, setSignatureType] = useState('drawn'); // drawn or typed
  const [typedSignature, setTypedSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Canvas ref for drawing
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    fetchSigningData();
  }, [token]);

  useEffect(() => {
    if (canvasRef.current && signatureType === 'drawn') {
      initCanvas();
    }
  }, [signatureType, data]);

  const fetchSigningData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/sign/${token}`);
      if (response.data.status === 'already_signed') {
        setAlreadySigned(true);
        setSignedAt(response.data.signed_at);
      } else {
        setData(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired signing link');
    } finally {
      setLoading(false);
    }
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const startDrawing = (e) => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawing || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    if (!canvasRef.current) return;
    initCanvas();
    setHasDrawn(false);
  };

  const handleSubmit = async () => {
    let signatureData = '';
    
    if (signatureType === 'drawn') {
      if (!hasDrawn) {
        toast.error('Please draw your signature');
        return;
      }
      signatureData = canvasRef.current.toDataURL('image/png');
    } else {
      if (!typedSignature.trim()) {
        toast.error('Please type your signature');
        return;
      }
      signatureData = typedSignature.trim();
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/sign/${token}`, {
        signature_type: signatureType,
        signature_data: signatureData,
      });
      setSubmitted(true);
      toast.success('Document signed successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit signature');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Document</h1>
          <p className="text-gray-600">{error}</p>
        </Card>
      </div>
    );
  }

  if (alreadySigned) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Document Already Signed</h1>
          <p className="text-gray-600">
            This document was signed on {signedAt ? new Date(signedAt).toLocaleString() : 'a previous date'}.
          </p>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600">
            Your signature has been submitted successfully. You may close this window.
          </p>
        </Card>
      </div>
    );
  }

  const { request, document } = data;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-3xl font-bold text-primary tracking-tight mb-2">
            Elite Home Rehab
          </div>
          <p className="text-gray-600">Document Signing</p>
        </div>

        {/* Document Info */}
        <Card className="p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <FileText className="text-primary" size={24} />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold">{request.document_name}</h2>
              <p className="text-gray-600 mt-1">
                Requested for: <strong>{request.recipient_name}</strong>
              </p>
              <p className="text-sm text-gray-500">{request.recipient_email}</p>
            </div>
          </div>
        </Card>

        {/* Document Content (for templates) */}
        {request.filled_content && (
          <Card className="p-6 mb-6">
            <h3 className="font-semibold mb-4">Document Content</h3>
            <div className="bg-white border rounded-lg p-6 whitespace-pre-wrap font-serif text-gray-800 leading-relaxed">
              {request.filled_content}
            </div>
          </Card>
        )}

        {/* For uploaded documents - show download link */}
        {document.document_type === 'upload' && (
          <Card className="p-6 mb-6">
            <h3 className="font-semibold mb-4">Document</h3>
            <p className="text-gray-600 mb-4">
              Please review the document before signing.
            </p>
            <Button
              variant="outline"
              onClick={() => window.open(`${BACKEND_URL}/api/admin/signature-documents/${document.id}/download?token=${encodeURIComponent(localStorage.getItem('token') || '')}`, '_blank')}
            >
              View Document
            </Button>
          </Card>
        )}

        {/* Signature Section */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Your Signature</h3>
          
          {/* Signature Type Toggle */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={signatureType === 'drawn' ? 'default' : 'outline'}
              onClick={() => setSignatureType('drawn')}
              size="sm"
            >
              <Edit2 size={14} className="mr-2" />
              Draw
            </Button>
            <Button
              variant={signatureType === 'typed' ? 'default' : 'outline'}
              onClick={() => setSignatureType('typed')}
              size="sm"
            >
              Type
            </Button>
          </div>

          {signatureType === 'drawn' ? (
            <div>
              <p className="text-sm text-gray-600 mb-2">Draw your signature below:</p>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 bg-white">
                <canvas
                  ref={canvasRef}
                  width={500}
                  height={150}
                  className="w-full cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearCanvas}
                className="mt-2"
              >
                <Eraser size={14} className="mr-2" />
                Clear
              </Button>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-2">Type your full legal name:</p>
              <Input
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                placeholder="Your full name"
                className="text-lg"
              />
              {typedSignature && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                  <p className="text-sm text-gray-500 mb-2">Preview:</p>
                  <p className="text-2xl font-signature italic text-gray-800">
                    {typedSignature}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Agreement */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              By clicking "Sign Document", I agree that my signature above is the legal equivalent 
              of my handwritten signature and I consent to be legally bound by this document.
            </p>
          </div>

          {/* Submit Button */}
          <div className="mt-6">
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full"
              size="lg"
            >
              {submitting ? 'Submitting...' : 'Sign Document'}
            </Button>
          </div>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-8">
          Powered by Elite Home Rehab HRIS
        </p>
      </div>
    </div>
  );
}
